// DiagiaInvitations — the "received campaigns" page for any logged-in user.
//
// Lists campaigns where the user has been invited (matched by JWT email).
// Each invitation is a card with the campaign name + scope + description and
// two actions: Accept (→ launch the diagnostic) or Refuser.
//
// After acceptance, "Lancer le diagnostic" routes to /diagia/new with both
// the campaign id and the invite token so the participant gets flipped to
// completed once they finish.

import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  FaSpinner,
  FaCheck,
  FaTimes,
  FaArrowRight,
  FaEnvelopeOpenText,
  FaCheckCircle,
} from 'react-icons/fa';

import useDiagiaApi from '../hooks/useDiagiaApi';

export default function DiagiaInvitations() {
  const { t } = useTranslation();
  const api = useDiagiaApi();
  const navigate = useNavigate();

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [acting, setActing] = useState({}); // {token: true} while a button is busy

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await api.listMyInvitations();
      setItems(data?.items || []);
    } catch (err) {
      setError(err?.response?.data?.error || 'Erreur de chargement');
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => { load(); }, [load]);

  const setBusy = (token, busy) =>
    setActing((prev) => ({ ...prev, [token]: busy }));

  const accept = async (inv) => {
    setBusy(inv.token, true);
    try {
      await api.acceptInvitation(inv.token);
      // Go straight into the diagnostic — token + campaign both threaded
      // through so the completion writes back to the participant.
      navigate(
        `/diagia/new?campaign=${inv.campaign_id}&token=${inv.token}`,
      );
    } catch (e) {
      window.alert(e?.response?.data?.error || 'Impossible d\'accepter');
      setBusy(inv.token, false);
    }
  };

  const decline = async (inv) => {
    const confirmed = window.confirm(
      `Refuser l'invitation à la campagne « ${inv.campaign_name} » ?`,
    );
    if (!confirmed) return;
    setBusy(inv.token, true);
    try {
      await api.declineInvitation(inv.token);
      await load();
    } catch (e) {
      window.alert(e?.response?.data?.error || 'Impossible de refuser');
      setBusy(inv.token, false);
    }
  };

  const startAccepted = (inv) =>
    navigate(`/diagia/new?campaign=${inv.campaign_id}&token=${inv.token}`);

  const pending  = items.filter((i) => i.status === 'invited');
  const accepted = items.filter((i) => i.status === 'accepted');
  const done     = items.filter((i) => i.status === 'completed');

  return (
    <div className="dia-page">
      <header className="dia-page-head">
        <div>
          <span className="dia-section-eyebrow">{t('diagia.inv.eyebrow', 'Mon espace')}</span>
          <h1>{t('diagia.nav.invitations', 'Mes invitations')}</h1>
          <p>{t('diagia.inv.sub', 'Les campagnes auxquelles vous avez été invité.')}</p>
        </div>
      </header>

      {loading && <p className="dia-empty">{t('diagia.home.loading', 'Chargement…')}</p>}
      {!loading && error && <p className="dia-error">{error}</p>}

      {!loading && !error && items.length === 0 && (
        <div className="dia-empty-card">
          <FaEnvelopeOpenText size={28} />
          <h3>{t('diagia.inv.empty', "Pas d'invitation pour l'instant")}</h3>
          <p>
            Lorsqu'un administrateur vous invitera à une campagne,
            elle apparaîtra ici.
          </p>
        </div>
      )}

      {pending.length > 0 && (
        <Section title={t('diagia.inv.pending', 'En attente de votre réponse')} count={pending.length} tone="warm">
          {pending.map((inv) => (
            <article key={inv.token} className="dia-invite-row">
              <div className="dia-invite-meta">
                <h3>{inv.campaign_name}</h3>
                {inv.campaign_scope && <p className="dia-invite-scope">{inv.campaign_scope}</p>}
                {inv.campaign_description && (
                  <p className="dia-invite-desc">{inv.campaign_description}</p>
                )}
                <p className="dia-table-muted" style={{ fontSize: '0.82rem' }}>
                  Invité le {new Date(inv.invited_at).toLocaleDateString('fr-FR')}
                </p>
              </div>
              <div className="dia-invite-actions">
                <button
                  type="button"
                  className="dia-cta dia-cta-ghost dia-cta-danger"
                  onClick={() => decline(inv)}
                  disabled={acting[inv.token]}
                >
                  <FaTimes /> Refuser
                </button>
                <button
                  type="button"
                  className="dia-cta dia-cta-primary"
                  onClick={() => accept(inv)}
                  disabled={acting[inv.token]}
                >
                  {acting[inv.token] ? <FaSpinner className="dia-spin" /> : <FaCheck />}
                  Accepter
                </button>
              </div>
            </article>
          ))}
        </Section>
      )}

      {accepted.length > 0 && (
        <Section title={t('diagia.inv.accepted', 'Acceptées — diagnostic à compléter')} count={accepted.length} tone="mint">
          {accepted.map((inv) => (
            <article key={inv.token} className="dia-invite-row">
              <div className="dia-invite-meta">
                <h3>{inv.campaign_name}</h3>
                {inv.campaign_scope && <p className="dia-invite-scope">{inv.campaign_scope}</p>}
                <p className="dia-table-muted" style={{ fontSize: '0.82rem' }}>
                  Acceptée le {new Date(inv.accepted_at).toLocaleDateString('fr-FR')}
                </p>
              </div>
              <div className="dia-invite-actions">
                <button
                  type="button"
                  className="dia-cta dia-cta-primary"
                  onClick={() => startAccepted(inv)}
                >
                  Démarrer le diagnostic <FaArrowRight />
                </button>
              </div>
            </article>
          ))}
        </Section>
      )}

      {done.length > 0 && (
        <Section title={t('diagia.inv.done', 'Diagnostics complétés')} count={done.length} tone="mist">
          {done.map((inv) => (
            <article key={inv.token} className="dia-invite-row dia-invite-done">
              <div className="dia-invite-meta">
                <h3>{inv.campaign_name}</h3>
                <p className="dia-table-muted" style={{ fontSize: '0.82rem' }}>
                  Complété le {new Date(inv.completed_at).toLocaleDateString('fr-FR')}
                </p>
              </div>
              <div className="dia-invite-actions">
                <FaCheckCircle size={22} className="dia-invite-check" />
              </div>
            </article>
          ))}
        </Section>
      )}
    </div>
  );
}

function Section({ title, count, tone, children }) {
  return (
    <section className={`dia-invite-section dia-invite-${tone}`}>
      <header>
        <h2>{title}</h2>
        <span>{count}</span>
      </header>
      <div className="dia-invite-stack">{children}</div>
    </section>
  );
}
